import dayjs from 'dayjs';

import { providerLogoFor } from '../../../common/utils/providerLogo';
import { estimateFlightMinutes } from './flightTime';
import { departureLocalOf, legInstantOf } from './localTime';

import type {
  LaneLeg,
  LaneMode,
  LaneSearch,
  NormalizedQuote,
} from '../../../apis/lanePlanner';
import {
  airRiskDurationFraction,
  assignMockScores,
  LaneScoreMode,
  oceanRiskDurationFraction,
} from './quoteScores';

/** Order of the per-mode sections, matching the mode tiles. */
export const MODE_ORDER: LaneMode[] = ['air', 'ocean', 'road'];

/** Order of the result sections (the Figma: Ocean, then Flight, then Road). */
const SECTION_MODE_ORDER: LaneMode[] = ['ocean', 'air', 'road'];

export const MODE_TITLES: Record<LaneMode, string> = {
  air: 'Flight',
  ocean: 'Ocean',
  road: 'Road',
};

const BEST_COUNT = 2;
/** Rows a mode section shows before "See more". */
export const SECTION_PREVIEW = 3;
const REF_MAX_LENGTH = 18;

const priceOf = (quote: NormalizedQuote): number =>
  quote.price == null ? Infinity : Number(quote.price);

/** The displayed duration (estimates count as stamped); quotes without one sort last. */
export const transitOf = (quote: NormalizedQuote): number =>
  travelOf(quote)?.minutes ?? Infinity;

export const departureOf = (quote: NormalizedQuote): string | null =>
  quote.legs.find((leg) => leg.departs_at)?.departs_at ?? null;

/** USD first, then alphabetical; quotes without a currency last. */
const currencyRank = (quote: NormalizedQuote): string =>
  quote.currency === 'USD' ? '0' : quote.currency ? `1${quote.currency}` : '2';

/**
 * Orders two quotes by price. Prices are only compared within one currency
 * (there is no conversion), so quotes group by currency first, and quotes
 * without a price always come last.
 */
export const comparePrice = (
  a: NormalizedQuote,
  b: NormalizedQuote
): number => {
  if ((a.price == null) !== (b.price == null)) return a.price == null ? 1 : -1;
  const byCurrency = currencyRank(a).localeCompare(currencyRank(b));
  if (byCurrency !== 0) return byCurrency;
  const byPrice = priceOf(a) - priceOf(b);
  return Number.isNaN(byPrice) ? 0 : byPrice;
};

/** Cheapest first, then fastest, then earliest departure; missing values last. */
export const sortBest = (quotes: NormalizedQuote[]): NormalizedQuote[] =>
  [...quotes].sort((a, b) => {
    const byPrice = comparePrice(a, b);
    if (byPrice !== 0) return byPrice;
    const byTransit = transitOf(a) - transitOf(b);
    if (byTransit !== 0 && !Number.isNaN(byTransit)) return byTransit;
    const da = departureOf(a);
    const db = departureOf(b);
    if (da && db) return da.localeCompare(db);
    return da ? -1 : db ? 1 : 0;
  });

export interface ResultSection {
  key: string;
  title: string;
  quotes: NormalizedQuote[];
  /** Rows beyond the preview, behind "See more". */
  hidden: number;
}

/**
 * Splits quotes into the Figma sections: "Best options" (cheapest across all
 * modes), then one section per mode (Ocean, Flight, Road), or, for a single mode, "Other options".
 */
export const buildSections = (
  quotes: NormalizedQuote[],
  expanded: ReadonlySet<string> = new Set(),
  sort: (quotes: NormalizedQuote[]) => NormalizedQuote[] = sortBest,
  topTitle = 'Best options'
): ResultSection[] => {
  if (!quotes.length) return [];
  const sorted = sort(quotes);
  const best = sorted.slice(0, BEST_COUNT);
  const sections: ResultSection[] = [
    { key: 'best', title: topTitle, quotes: best, hidden: 0 },
  ];
  const modes = SECTION_MODE_ORDER.filter((mode) =>
    quotes.some((quote) => quote.mode === mode)
  );
  if (modes.length <= 1) {
    // A lane with several airports or vendors can return hundreds of rates:
    // the same preview and "See more" as the per-mode sections.
    const rest = sorted.slice(BEST_COUNT);
    if (rest.length) {
      // a calendar cell opens the section of its mode, which here is this one
      const open = expanded.has('other') || expanded.has(modes[0]);
      sections.push({
        key: 'other',
        title: 'Other options',
        quotes: open ? rest : rest.slice(0, SECTION_PREVIEW),
        hidden: open ? 0 : Math.max(rest.length - SECTION_PREVIEW, 0),
      });
    }
    return sections;
  }
  for (const mode of modes) {
    const all = sorted.filter((quote) => quote.mode === mode);
    const open = expanded.has(mode);
    sections.push({
      key: mode,
      title: MODE_TITLES[mode],
      quotes: open ? all : all.slice(0, SECTION_PREVIEW),
      hidden: open ? 0 : Math.max(all.length - SECTION_PREVIEW, 0),
    });
  }
  return sections;
};

const DURATION_UNITS = {
  short: { hours: 'h', minutes: 'm' },
  long: { hours: 'hr', minutes: 'min' },
};

/** "13 h" and "8 h 25 m" (the card header, the booking), or "8 hr 25 min" (the leg block, as the Figma writes it). */
export const formatTransit = (
  minutes?: number | null,
  style: keyof typeof DURATION_UNITS = 'short'
): string | null => {
  if (minutes == null || minutes <= 0) return null;
  const unit = DURATION_UNITS[style];
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} ${unit.minutes}`;
  return mins
    ? `${hours} ${unit.hours} ${mins} ${unit.minutes}`
    : `${hours} ${unit.hours}`;
};

/** "1 business day": road transit, which the vendor counts in business days and the API sends as days x 1440 minutes. */
export const formatBusinessDays = (minutes?: number | null): string | null => {
  if (minutes == null || minutes <= 0) return null;
  const days = Math.max(1, Math.round(minutes / 1440));
  return `${days} business day${days === 1 ? '' : 's'}`;
};

/** Minutes on the ground before leg `index`, between two absolute instants; null when a stamp or its zone is unknown or they are out of order. */
export const layoverMinutesOf = (
  quote: NormalizedQuote,
  index: number
): number | null => {
  const arrived = legInstantOf(quote, index - 1, 'arrives');
  const departed = legInstantOf(quote, index, 'departs');
  if (arrived == null || departed == null) return null;
  const minutes = Math.round((departed - arrived) / 60000);
  return minutes >= 0 ? minutes : null;
};

const approx = (text: string | null, estimated: boolean, tag = '') =>
  text && estimated ? `~${text}${tag}` : text;

/** One leg's time: its stamps, else the vendor's transit of a single-leg quote, else (air) a guess from its airports. */
const legTravelOf = (
  quote: NormalizedQuote,
  index: number
): { minutes: number; estimated: boolean; businessDays?: boolean } | null => {
  const leg = quote.legs[index];
  const departs = legInstantOf(quote, index, 'departs');
  const arrives = legInstantOf(quote, index, 'arrives');
  if (departs != null && arrives != null) {
    const minutes = Math.round((arrives - departs) / 60000);
    if (minutes > 0) return { minutes, estimated: false };
  }
  if (quote.legs.length === 1 && quote.transit_minutes) {
    return {
      minutes: quote.transit_minutes,
      estimated: false,
      businessDays: quote.mode === 'road',
    };
  }
  const guess =
    leg?.mode === 'air'
      ? estimateFlightMinutes(leg.origin, leg.destination)
      : null;
  return guess ? { minutes: guess, estimated: true } : null;
};

/** "5 hr 30 min", or "~5 hr 30 min (est.)" for a guess. */
export const legTravelLabelOf = (
  quote: NormalizedQuote,
  index: number
): string | null => {
  const leg = legTravelOf(quote, index);
  return approx(
    leg?.businessDays
      ? formatBusinessDays(leg.minutes)
      : formatTransit(leg?.minutes, 'long'),
    !!leg?.estimated,
    ' (est.)'
  );
};

export interface Travel {
  /** "13 h" or "~9 h 10 m". */
  label: string;
  /** The minutes behind the label, for sorting. */
  minutes: number;
  /** What the label leaves out or guesses: "Estimated, excl. layovers". */
  note: string | null;
}

/**
 * The quote's total, computed once for the card: the vendor's transit when it
 * gives one, else its legs added up, plus layovers
 * where both stamps resolve. Legs that can't be timed are left out (a partial
 * total, noted); null when none can.
 */
export const travelOf = (quote: NormalizedQuote): Travel | null => {
  if (quote.transit_minutes) {
    const label =
      quote.mode === 'road'
        ? formatBusinessDays(quote.transit_minutes)
        : formatTransit(quote.transit_minutes);
    return label ? { label, minutes: quote.transit_minutes, note: null } : null;
  }
  const guess =
    quote.mode === 'air' && quote.route && !quote.legs.length
      ? estimateFlightMinutes(quote.route.origin, quote.route.destination)
      : null;
  const legs = guess
    ? [{ minutes: guess, estimated: true }]
    : quote.legs.map((_, index) => legTravelOf(quote, index));
  const timed = legs.filter((leg) => leg != null);
  if (!timed.length) return null;
  let minutes = timed.reduce((sum, leg) => sum + leg.minutes, 0);
  let layoversUnknown = false;
  for (let index = 1; index < legs.length; index += 1) {
    const layover = layoverMinutesOf(quote, index);
    if (layover == null) layoversUnknown = true;
    else minutes += layover;
  }
  const estimated = timed.some((leg) => leg.estimated);
  const skipped = quote.legs.filter((_, index) => !legs[index]);
  const skippedAreGround =
    skipped.length > 0 && skipped.every((leg) => leg.mode !== 'air');
  // priced pickup and delivery trucking live outside `legs` and are never in the total
  const hasTrucking = !!(
    quote.door_to_door?.pickup || quote.door_to_door?.delivery
  );
  const notes = [
    estimated && 'Estimated',
    layoversUnknown && 'excl. layovers',
    (skippedAreGround || hasTrucking) && 'excl. ground legs',
    skipped.length > 0 && !skippedAreGround && 'excl. untimed legs',
  ].filter(Boolean);
  const label = approx(formatTransit(minutes), estimated);
  return label ? { label, minutes, note: notes.join(', ') || null } : null;
};

/** "1 h (excl. layovers)": the label with what it leaves out, for text that has no second line. */
export const travelTextOf = (quote: NormalizedQuote): string | null => {
  const travel = travelOf(quote);
  return (
    travel && (travel.note ? `${travel.label} (${travel.note})` : travel.label)
  );
};

/** "Thu, 10/22/26": the pickup day of a road quote, which has no departure of its own (the search's ship date). */
export const pickupLabelOf = (
  quote: NormalizedQuote,
  shipDate?: string
): string | null => {
  if (
    quote.mode !== 'road' ||
    !shipDate ||
    quote.legs.some((l) => l.departs_at)
  ) {
    return null;
  }
  const day = dayjs(shipDate);
  return day.isValid() ? day.format('ddd, M/D/YY') : null;
};

/** The carrier's service level ("Standard LTL Guaranteed"), which tells a carrier's road quotes apart. */
export const serviceLevelOf = (quote: NormalizedQuote): string | null => {
  const level = quote.details?.service_level;
  return quote.mode === 'road' && typeof level === 'string' && level
    ? level
    : null;
};

export const formatMoney = (
  amount: string | number | null | undefined,
  currency: string | null | undefined
): string => {
  if (amount == null || amount === '') return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(Number(amount));
  } catch {
    return `${amount} ${currency ?? ''}`.trim();
  }
};

/** Stops between origin and destination, or null when the quote has no itinerary (tariffs, road). */
export const stopsOf = (quote: NormalizedQuote): number | null =>
  quote.mode !== 'road' && quote.legs.length ? quote.legs.length - 1 : null;

export const stopsLabel = (quote: NormalizedQuote): string | null => {
  const stops = stopsOf(quote);
  if (stops == null) return null;
  return stops === 0 ? 'Non stop' : `${stops} stop${stops > 1 ? 's' : ''}`;
};

export const carrierNameOf = (quote: NormalizedQuote): string | null =>
  quote.carrier || quote.legs.find((leg) => leg.carrier)?.carrier || null;

export const carrierOf = (quote: NormalizedQuote): string =>
  carrierNameOf(quote) ?? 'Carrier';

/** A leg's logo: the vendor's own, else the product's logo for its carrier by name. */
export const legLogoOf = (leg: LaneLeg): string | null =>
  leg.logo_url || providerLogoFor(leg.carrier, leg.mode);

/** Distinct carrier logos of the quote's legs, for multi-carrier itineraries. */
export const logosOf = (quote: NormalizedQuote): string[] => [
  ...new Set(quote.legs.map(legLogoOf).filter((url): url is string => !!url)),
];

/** Shortened vendor reference for the card, with the full one kept for a tooltip. */
export const shortRef = (quote: NormalizedQuote): string =>
  quote.vendor_quote_id.length > REF_MAX_LENGTH
    ? `${quote.vendor_quote_id.slice(0, REF_MAX_LENGTH)}…`
    : quote.vendor_quote_id;

/** "JFK → PHL": the airport or port pair the quote was searched for, else its first and last leg. */
export const routeOf = (quote: NormalizedQuote): string | null => {
  if (quote.route) return `${quote.route.origin} → ${quote.route.destination}`;
  const first = quote.legs[0]?.origin;
  const last = quote.legs[quote.legs.length - 1]?.destination;
  return first && last ? `${first} → ${last}` : null;
};

export const vendorNameOf = (search: LaneSearch, vendor: string): string =>
  search.vendors.find((v) => v.vendor === vendor)?.display_name ?? vendor;

/** "10:30 AM EDT" and "Wed, 8/19/26" for the card's departure column: the local time at the origin. */
export const departureParts = (
  quote: NormalizedQuote
): { time: string; date: string } | null => {
  const local = departureLocalOf(quote);
  if (!local) return null;
  return {
    time: `${local.time}${local.zone ? ` ${local.zone}` : ''}`,
    date: local.date,
  };
};

/** The mode whose Risk wording applies; road reads like air (layovers and cross-docks). */
export const scoreModeOf = (quote: NormalizedQuote): LaneScoreMode =>
  quote.mode === 'ocean' ? 'ocean' : 'air';

/** Mock Risk %, stable for a quote and longer for slower transits (see quoteScores.ts). */
export const riskPercentOf = (quote: NormalizedQuote): number => {
  const fraction =
    quote.mode === 'ocean'
      ? oceanRiskDurationFraction(
          quote.transit_minutes == null ? null : quote.transit_minutes / 1440
        )
      : airRiskDurationFraction(quote.transit_minutes);
  return assignMockScores(quote.id, fraction).riskPercent;
};
